import { Plus, Send } from "lucide-react";
import type { FC } from "react";

import { CardGrid } from "@/components/surface";
import { Button } from "@/components/ui/button";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { ClientCards } from "@/features/dashboard/components/ClientCards";
import { DashboardShell } from "@/features/dashboard/components/DashboardShell";

import { PanelLoading } from "../../components/PanelState";
import { SectionHeader } from "../../components/SectionHeader";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { isOpenCaseStatus } from "../../lib/coverage";
import { getFullName } from "../../lib/format";
import { scheduleScrollToDomId } from "../../lib/scrollToDomId";
import { PERSONAL_SECTION_IDS } from "../personal/personalModel";
import { ApplicantSummaryCard } from "./ApplicantSummaryCard";
import { ClientIdentityHeader, StatusBanner } from "./ClientIdentityHeader";
import { CorrectionsReviewCard } from "./CorrectionsReviewCard";
import { SignatureRequestsCard } from "./SignatureRequestsCard";
import { DependentsPreviewCard } from "./DependentsPreviewCard";
import { GetStartedCard } from "./GetStartedCard";
import { PORTAL_ACCESS_DOM_ID, PortalAccessCard } from "./PortalAccessCard";
import { ReadinessCard } from "./ReadinessCard";

/**
 * Overview: the client dashboard. The Client Manager's composer with
 * recommended actions in the centre, what needs attention for this client
 * (reminders, expirations, deadlines, intakes, cases) below, and the live
 * activity feed in the right rail. Brand-new clients keep the onboarding
 * checklist as the first card. The section header is the dashboard's own,
 * carrying New Request / New Case next to the rail toggle.
 */
export const OverviewTab: FC = () => {
  const {
    clientId,
    client,
    isLoadingClient,
    matters,
    badges,
    onboarding,
    createCase,
    url,
    openPersonalSection
  } = useClientProfile();
  const { specFor } = useAgentSpecs();

  const fullName = client ? getFullName(client) || "Unknown" : "";
  const firstName = (client?.first_name ?? "").trim() || null;
  const { highlightedId } = useFocusScroll(
    url.state.focus.portal ? PORTAL_ACCESS_DOM_ID : null,
    Boolean(client) && !onboarding.pending
  );
  const newestVisa =
    (matters.newestMatter?.case?.visa ?? "").toString().trim() || null;

  const startCase = () => createCase.open({ returnToOverview: true });
  const newRequest = () => {
    if (newestVisa) url.openDocsOnlyIntakeForVisa(newestVisa);
    else url.openCreateIntake({ templateHint: "documents" });
  };

  const actions = (
    <>
      <Button
        variant="ghost"
        size="xs"
        className="text-muted-foreground hover:text-foreground"
        onClick={() => url.openCreateIntake()}
      >
        <Send /> New Request
      </Button>
      <Button variant="primary" size="xs" onClick={startCase}>
        <Plus /> New Case
      </Button>
    </>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {onboarding.pending ? (
        <>
          <SectionHeader title="Overview" actions={actions} />
          <PanelLoading text="Loading client profile..." />
        </>
      ) : (
        <DashboardShell
          mode="client"
          agent="client"
          scope={{ kind: "client", id: clientId, displayName: fullName }}
          spec={specFor("assistant")}
          clientId={clientId}
          title="Overview"
          actions={actions}
          subject={firstName}
          className="min-h-0 flex-1"
          identity={(state) => (
            <div className="flex flex-col gap-3.5">
              <ClientIdentityHeader
                clientId={clientId}
                client={client}
                health={state.summary?.status_health}
                onOpenPersonal={() => openPersonalSection()}
                onOpenPassports={() => url.setTab("passports")}
                onOpenPortal={() => scheduleScrollToDomId(PORTAL_ACCESS_DOM_ID)}
              />
              <StatusBanner
                clientId={clientId}
                firmId={client?.law_firm_id}
                health={state.summary?.status_health}
                firstName={firstName}
                status={client?.current_immigration_status}
                onAddStatus={() => url.setTab("immigration_overview")}
                onAutofill={() => url.setTab("personal", { autofill: "1" })}
                onAsk={state.prompt}
              />
            </div>
          )}
          leadingCards={
            <>
              <CorrectionsReviewCard />
              <SignatureRequestsCard />
              {onboarding.show ? (
                <GetStartedCard
                  clientFullName={fullName}
                  clientFirstName={firstName ?? ""}
                  casesCount={matters.matters.length}
                  intakesCount={badges.intakesCount ?? 0}
                  visaTypeLabel={newestVisa}
                  onStartCase={startCase}
                  onEditCase={() => {
                    const newest = matters.newestMatter;
                    if (!newest) return;
                    createCase.open({
                      prefillMatter: newest,
                      replaceMatter: newest,
                      returnToOverview: true
                    });
                  }}
                  onNewRequest={newRequest}
                />
              ) : null}
              {client ? (
                <PortalAccessCard
                  clientId={clientId}
                  clientName={fullName || "this client"}
                  firstName={firstName ?? "They"}
                  archived={Boolean(client.archived)}
                  preferredLanguage={client.preferred_language ?? null}
                  onOpenPersonal={() =>
                    openPersonalSection(PERSONAL_SECTION_IDS.contact)
                  }
                  highlighted={highlightedId === PORTAL_ACCESS_DOM_ID}
                />
              ) : null}
            </>
          }
          cards={(state) => (
            <>
              <ClientCards
                {...state}
                clientId={clientId}
                url={url}
                readiness={
                  matters.matters.some((m) =>
                    isOpenCaseStatus(m.case?.status)
                  ) ? (
                    <ReadinessCard
                      health={state.summary?.status_health}
                      onOpen={(target) => {
                        if (target === "personal") openPersonalSection();
                        else if (target === "passports")
                          url.setTab("passports");
                        else url.setTab("immigration_overview");
                      }}
                    />
                  ) : null
                }
              />
              <CardGrid cols={2} data-testid="profile-cards">
                <ApplicantSummaryCard
                  client={client}
                  isLoading={isLoadingClient && !client}
                  onOpenPersonalSection={openPersonalSection}
                />
                <DependentsPreviewCard
                  clientId={clientId}
                  onOpenDependents={(dependentId) =>
                    url.setTab(
                      "dependents",
                      dependentId ? { dependentId } : undefined
                    )
                  }
                />
              </CardGrid>
            </>
          )}
        />
      )}
    </div>
  );
};
